import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '@/utils/seo';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantHero from '@/components/feature/ElegantHero';

export default function PrivacyPage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };

  useSEO({
    title: t('seo.privacyTitle'),
    description: t('seo.privacyDescription'),
    keywords: t('seo.privacyKeywords'),
    path: '/privacy',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(t('seo.privacyTitle'), t('seo.privacyDescription'), '/privacy', getSchemaLang()),
        generateBreadcrumbSchema([
          { name: t('nav.topPage'), path: '/' },
          { name: t('footer.privacy'), path: '/privacy' },
        ]),
      ],
    },
  });

  const sections = [
    {
      title: '法令等の遵守',
      body: '１．当社は、お客様の個人情報の取得、利用その他一切の個人情報の取扱いについて、個人情報の保護に関する法律、通信の秘密に係る電気通信事業法の規定その他の関連法令、電気通信事業における個人情報保護に関するガイドライン（以下、「ガイドライン」といいます）、及びこのプライバシーポリシーを遵守します。',
    },
    {
      title: '適正な取得',
      body: '５．当社は、偽りその他不正の手段により個人情報を取得しません。',
    },
    {
      title: '安全管理措置',
      body: '７．当社は、お客様の個人情報を正確かつ最新の内容に保つよう努めるとともに、不正なアクセス、改ざん、漏えい、滅失及び毀損から保護するため、必要かつ適切な安全管理措置を講じます。',
    },
    {
      title: '従業者の監督',
      body: '８．当社は、お客様の個人情報の安全管理が図られるよう従業者に対する必要かつ適切な監督をします。また、従業者に対して個人情報の適正な取扱いの確保のために必要な教育研修を実施します。',
    },
    {
      title: '継続的改善',
      body: '１５．当社は、個人情報保護に関する内部規定の整備、従業者教育及び内部監査の実施などを通じて、社内における個人情報の取扱いについて継続的な改善に努めます。',
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      <ElegantHero
        title="プライバシーポリシー"
        subtitle="個人情報の取扱いに関する方針"
        tag="Privacy Policy"
        breadcrumb={[
          { label: t('nav.topPage'), to: '/' },
          { label: t('footer.privacy') },
        ]}
        minHeight="360px"
      />

      <main className="py-16 md:py-24 px-6 md:px-10 max-w-[800px] mx-auto">
        <p
          className="text-sm mb-12"
          style={{
            color: '#A8A294',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          制定：平成30年2月21日
        </p>

        <div className="space-y-12">
          <section>
            <p
              className="text-sm leading-relaxed"
              style={{
                color: '#7A7569',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              当社は、個人情報の取扱いに関する方針を、次のとおり定め、これを公表するとともに遵守することを宣言します。
            </p>
          </section>

          {sections.map((sec, i) => (
            <section key={i}>
              <h2
                className="text-base mb-4 flex items-center gap-3"
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  fontWeight: 500,
                  color: '#1A1A1A',
                }}
              >
                <span
                  className="w-6 h-px inline-block shrink-0"
                  style={{ background: '#C9A227' }}
                />
                ●{sec.title}
              </h2>
              <p
                className="text-sm leading-relaxed pl-9"
                style={{
                  color: '#7A7569',
                  fontFamily: "'Noto Serif JP', serif",
                }}
              >
                {sec.body}
              </p>
            </section>
          ))}

          <section>
            <h2
              className="text-base mb-4 flex items-center gap-3"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
              }}
            >
              <span
                className="w-6 h-px inline-block shrink-0"
                style={{ background: '#C9A227' }}
              />
              ●利用者の権利利益の保護に関する取組
            </h2>
            <p
              className="text-sm leading-relaxed pl-9 mb-4"
              style={{
                color: '#7A7569',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              ２．当社は、個人情報保護に関する利用者の権利利益の保護のための主な取組として次を実施しています。
            </p>
            <div
              className="ml-9 p-5 border"
              style={{
                borderColor: 'rgba(212, 207, 196, 0.4)',
                background: '#FFFFFF',
              }}
            >
              <div
                className="space-y-3 text-sm"
                style={{
                  color: '#7A7569',
                  fontFamily: "'Noto Serif JP', serif",
                }}
              >
                <p>①本人の求めがあった場合、ダイレクト・メールの発送停止や電話勧奨の停止措置等を行います。（詳しくは info@tradfit.com にお問い合わせ下さい。）</p>
                <p>②当社は個人情報の取扱いの全部又は一部を利用目的の範囲内で委託します。</p>
                <ul className="list-none ml-4 space-y-1" style={{ color: '#A8A294' }}>
                  <li>◎各種商品・サービスの販売・受付業務</li>
                  <li>◎故障修理業務</li>
                  <li>◎料金関連業務</li>
                  <li>◎マーケティング業務</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2
              className="text-base mb-4 flex items-center gap-3"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
              }}
            >
              <span
                className="w-6 h-px inline-block shrink-0"
                style={{ background: '#C9A227' }}
              />
              ●利用目的の特定及び公表
            </h2>
            <p
              className="text-sm leading-relaxed pl-9"
              style={{
                color: '#7A7569',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              ３．当社は、当社が取得したお客様の個人情報の利用目的をできる限り特定の上、あらかじめ公表します。また、お客様から契約書等の書面に記載された個人情報を直接取得する場合は、あらかじめお客様に対して利用目的を明示します。
            </p>
          </section>

          <section>
            <h2
              className="text-base mb-4 flex items-center gap-3"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
              }}
            >
              <span
                className="w-6 h-px inline-block shrink-0"
                style={{ background: '#C9A227' }}
              />
              ●保存期間
            </h2>
            <p
              className="text-sm leading-relaxed pl-9 mb-3"
              style={{
                color: '#7A7569',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              ６．当社は、利用目的に必要な範囲内でお客様の個人情報の保存期間を定め、保存期間経過後又は利用目的達成後はお客様の個人情報を遅滞なく消去いたします。ただし、次の各号に該当する場合はこの限りではありません。
            </p>
            <ul
              className="list-none ml-9 space-y-1 text-sm"
              style={{ color: '#A8A294', fontFamily: "'Noto Serif JP', serif" }}
            >
              <li>１）法令の規定に基づき、保存しなければならないとき。</li>
              <li>２）本人の同意があるとき。</li>
            </ul>
          </section>

          <section>
            <h2
              className="text-base mb-4 flex items-center gap-3"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
              }}
            >
              <span
                className="w-6 h-px inline-block shrink-0"
                style={{ background: '#C9A227' }}
              />
              ●第三者への提供
            </h2>
            <p
              className="text-sm leading-relaxed pl-9 mb-3"
              style={{
                color: '#7A7569',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              １０．当社は、次の各号に掲げる場合を除き、お客様の同意を得ないで、第三者にお客様の個人情報を提供することはしません。
            </p>
            <ul
              className="list-none ml-9 space-y-1 text-sm"
              style={{ color: '#A8A294', fontFamily: "'Noto Serif JP', serif" }}
            >
              <li>１）法令に基づく場合</li>
              <li>２）人の生命、身体又は財産の保護のために必要がある場合</li>
              <li>３）公衆衛生の向上又は児童の健全な育成の推進のために特に必要がある場合</li>
              <li>４）国の機関若しくは地方公共団体が法令に定める事務をすることに対して協力する必要がある場合</li>
            </ul>
          </section>

          <section>
            <h2
              className="text-base mb-4 flex items-center gap-3"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
              }}
            >
              <span
                className="w-6 h-px inline-block shrink-0"
                style={{ background: '#C9A227' }}
              />
              ●開示等の求め
            </h2>
            <p
              className="text-sm leading-relaxed pl-9"
              style={{
                color: '#7A7569',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              １１．お客様が個人情報の利用目的の通知、又は個人情報の開示、又は訂正、追加若しくは削除又は利用の停止若しくは第三者への提供の停止を希望される場合は、当社が別に定める手続に従って下さい。
            </p>
          </section>

          <section>
            <h2
              className="text-base mb-4 flex items-center gap-3"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
              }}
            >
              <span
                className="w-6 h-px inline-block shrink-0"
                style={{ background: '#C9A227' }}
              />
              ●苦情の処理
            </h2>
            <p
              className="text-sm leading-relaxed pl-9"
              style={{
                color: '#7A7569',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              １２．当社は、個人情報の取扱いに関するお客様からの苦情その他のお問い合わせについて迅速かつ適切に対応いたします。（対応窓口：
              <a
                href="mailto:info@tradfit.com"
                className="no-underline transition-colors duration-300"
                style={{ color: '#C9A227' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
                }}
              >
                info@tradfit.com
              </a>
              ）
            </p>
          </section>

          <section>
            <h2
              className="text-base mb-4 flex items-center gap-3"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
              }}
            >
              <span
                className="w-6 h-px inline-block shrink-0"
                style={{ background: '#C9A227' }}
              />
              ●漏えい発生時の対応
            </h2>
            <p
              className="text-sm leading-relaxed pl-9"
              style={{
                color: '#7A7569',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              １４．お客様の個人情報の漏えい等が発生した場合には、事実関係を速やかにお客様に通知するなど適切な対応を行います。
            </p>
          </section>

          <section
            className="pt-10 border-t"
            style={{ borderColor: 'rgba(212, 207, 196, 0.4)' }}
          >
            <p
              className="text-right text-sm"
              style={{
                color: '#3E3B34',
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
              }}
            >
              制定：平成30年2月21日
              <br />
              TradFit株式会社　代表取締役社長 戸田良樹
            </p>
          </section>
        </div>
      </main>

      <ElegantFooter />
    </div>
  );
}
